import React, { useEffect, useState } from "react";
import OTPInput from "react-otp-input";
import { useTranslation } from "react-i18next";
import {
    Identifier,
    VerifyResult,
    errorKey,
    sendDeletionOtp,
    verifyDeletionOtp,
} from "../../api/accountDeletion";
import { errorText, linkButton, primaryButton } from "./styles";

const RESEND_SECONDS = 60;

type Props = {
    identifier: Identifier;
    display: string;
    onVerified: (result: VerifyResult) => void;
    onBack: () => void;
};

const OtpStep: React.FC<Props> = ({ identifier, display, onVerified, onBack }) => {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft]);

    const verify = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            onVerified(await verifyDeletionOtp(identifier, code));
        } catch (err) {
            setError(t(`delete_account.errors.${errorKey(err)}`));
            setCode("");
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        setError(null);
        setCode("");
        try {
            await sendDeletionOtp(identifier);
            setSecondsLeft(RESEND_SECONDS);
        } catch (err) {
            setError(t(`delete_account.errors.${errorKey(err, "generic")}`));
        }
    };

    return (
        <form onSubmit={verify} className="space-y-5">
            <div>
                <h2 className="text-xl font-bold text-gray-900">{t("delete_account.otp_title")}</h2>
                <p className="mt-2 text-gray-600">
                    {t("delete_account.otp_sent", { target: display })}{" "}
                    <button type="button" onClick={onBack} className={linkButton}>
                        {t("delete_account.change")}
                    </button>
                </p>
            </div>

            <div dir="ltr" className="flex justify-center">
                <OTPInput
                    value={code}
                    onChange={setCode}
                    numInputs={4}
                    inputType="tel"
                    shouldAutoFocus
                    containerStyle="gap-3"
                    renderInput={(props) => (
                        <input
                            {...props}
                            style={undefined}
                            className="h-14 w-14 rounded-xl border border-gray-300 text-center text-2xl font-bold text-gray-900 focus:border-[#00B9EC] focus:outline-none focus:ring-1 focus:ring-[#00B9EC]"
                        />
                    )}
                />
            </div>

            {error && <p className={`${errorText} text-center`}>{error}</p>}

            <button type="submit" disabled={busy || code.length !== 4} className={primaryButton}>
                {t("delete_account.verify")}
            </button>

            <div className="flex flex-col items-center gap-2 text-center">
                <button type="button" onClick={resend} disabled={secondsLeft > 0} className={linkButton}>
                    {secondsLeft > 0
                        ? t("delete_account.resend_in", { seconds: secondsLeft })
                        : t("delete_account.resend")}
                </button>
                <p className="text-sm text-gray-500">{t("delete_account.no_code")}</p>
            </div>
        </form>
    );
};

export default OtpStep;
